import * as constants from 'spw-constants';
import { buildCurrentProjectSubcategoryColors } from 'spw-projects/space_types/utils';
import { IProjectsState } from './types';
import {
  Actions,
  CHANGE_BUILDING_DIALOG_STATE,
  CHANGE_CLIENT_DIALOG_STATE,
  CHANGE_PROJECT_DIALOG_STATE,
  CLEAN_UP_CURRENT_PROJECT,
  CLEAR_SELECTED_PROJECT,
  CLEAR_SPACE_TYPE_CATEGORIES,
  CREATE_USER_FAVORITE_PROJECT_SUCCEEDED,
  DELETE_USER_FAVORITE_PROJECT_SUCCEEDED,
  FETCH_PROJECT_COLORS_SUCCEEDED,
  GET_DEPARTMENTS_BY_PROJECT_ID_SUCCEEDED,
  GET_ORGANIZATION_USERS_SUCCEEDED,
  GET_PROJECT_EDITORS,
  GET_PROJECT_EDITORS_FAILED,
  GET_PROJECT_EDITORS_SUCCEEDED,
  GET_PROJECT_INFO_BY_ID_SUCCEEDED,
  GET_PROJECTS,
  GET_PROJECTS_SUCCEEDED,
  GET_SPACE_TYPE_CATEGORIES_SUCCEEDED,
  GET_USER_FAVORITE_PROJECTS_LEGACY,
  GET_USER_FAVORITE_PROJECTS_LEGACY_SUCCEEDED,
  JOIN_CURRENT_PROJECT_SUCCEEDED,
  JOIN_PROJECT_SUCCEEDED,
  LEAVE_PROJECT_SUCCEEDED,
  REMOVE_PROJECT_EDITOR,
  REMOVE_PROJECT_EDITOR_FAILED,
  REMOVE_PROJECT_EDITOR_SUCCEEDED,
  SAVE_PROJECT_SUCCEEDED,
  SET_CLONING_PROJECT_ID,
  SET_CURRENT_BUILDING,
  SET_CURRENT_FLOOR,
  SET_CURRENT_PROJECT,
  SET_CURRENT_PROJECT_COLORS,
  SET_PREVIOUSLY_VISITED_BENCHMARK_PROJECT_ID,
  SET_PROJECT_DETAIL_NEXT_SELECTED_TAB,
  SET_PROJECT_DETAIL_SELECTED_TAB,
  SET_PROJECT_TYPE,
  TOGGLE_HEADCOUNT_MODAL,
  TRANSFER_PROJECT_OWNERSHIP_SUCCEEDED,
  UPDATE_CURRENT_BUILDING,
  UPDATE_DEPARTMENTS_SUCCESS,
  UPDATE_NUMBER_OF_DEPARTMENTS,
  UPDATE_NUMBER_OF_SCENARIOS,
  UPDATE_PROJECT_BUILDINGS_SUCCEEDED,
  UPDATE_PROJECT_YEAR,
} from './actions';
import { initialProjectProfile } from './projects';

const initialState: IProjectsState = {
  cloningProjectId: null,
  currentProjectType: constants.EXISTING_SITE_ANALYSIS,
  currentProject: null,
  currentProjectColors: null,
  isBuildingModalOpen: false,
  showNewBuildingModal: false,
  isClientModalOpen: false,
  isProjectModalOpen: false,
  projects: [],
  totalFilteredProjects: 0,
  openHeadcountModal: false,
  currentBuilding: null,
  currentFloor: null,
  spaceTypeCategories: [],
  spaceTypeCategoriesMap: null,
  currentProjectProfile: initialProjectProfile,
  departments: [],
  colors: {
    [constants.CUSTOM_WORKSPACE_COLORS]: [],
    [constants.CUSTOM_MEETING_SPACE_COLORS]: [],
    [constants.CUSTOM_SUPPORT_SPACE_COLORS]: [],
    [constants.CUSTOM_AMENITY_SPACE_COLORS]: [],
    [constants.CUSTOM_CATEGORY_COLORS]: [],
  },
  userFavoriteProjectsLegacy: [],
  organizationUsers: [],
  projectDetailSelectedTab: 0,
  projectDetailNextTab: null,
  loadingProjects: false,
  projectEditorsList: {
    items: [],
    loading: false,
    queriedAtLeastOnce: false,
    total: 0,
  },
};

const reducer = (
  state: IProjectsState = initialState,
  action: Actions
): IProjectsState => {
  switch (action.type) {
    case SET_PROJECT_TYPE:
      return {
        ...state,
        currentProjectType: action.payload.projectType,
      };
    case SAVE_PROJECT_SUCCEEDED:
      return {
        ...state,
        currentFloor: null,
        currentBuilding: null,
        currentProject: action.payload.project,
        currentProjectProfile: action.payload.project,
        departments: [],
      };
    case GET_DEPARTMENTS_BY_PROJECT_ID_SUCCEEDED:
      return {
        ...state,
        departments: [...action.payload.departments],
      };
    case UPDATE_DEPARTMENTS_SUCCESS: {
      return {
        ...state,
        departments: [...action.payload.departments],
      };
    }
    case CHANGE_BUILDING_DIALOG_STATE:
      return {
        ...state,
        isBuildingModalOpen: action.payload.isOpen,
        showNewBuildingModal: action.payload.showNewBuildingModal,
        currentBuilding: action.payload.isOpen
          ? action.payload.building
          : state.currentBuilding,
      };
    case CHANGE_CLIENT_DIALOG_STATE:
      return {
        ...state,
        isClientModalOpen: action.payload.isOpen,
      };
    case CHANGE_PROJECT_DIALOG_STATE:
      return {
        ...state,
        isProjectModalOpen: action.payload.isOpen,
      };
    case GET_PROJECTS_SUCCEEDED:
      return {
        ...state,
        projects: [...state.projects, ...action.payload.projects],
        totalFilteredProjects: action.payload.totalFilteredProjects,
        loadingProjects: false,
      };
    case GET_PROJECTS:
      return {
        ...state,
        loadingProjects: true,
        projects: action.payload?.page > 1 ? state.projects : [],
      };
    case GET_SPACE_TYPE_CATEGORIES_SUCCEEDED:
      return {
        ...state,
        spaceTypeCategories: action.payload.spaceTypeCategories,
        spaceTypeCategoriesMap: action.payload.spaceTypeCategoriesMap,
      };
    case CLEAR_SPACE_TYPE_CATEGORIES:
      return {
        ...state,
        spaceTypeCategories: [],
        spaceTypeCategoriesMap: null,
      };
    case SET_CURRENT_PROJECT:
      return {
        ...state,
        currentProject: action.payload.project,
      };
    case SET_CLONING_PROJECT_ID:
      return {
        ...state,
        cloningProjectId: action.payload.projectId,
      };
    case SET_CURRENT_BUILDING: {
      const { building, keepFloor } = action.payload;

      const currentBuilding = building
        ? {
            ...building,
            numberOfFloors: building?.floors?.length,
          }
        : null;
      return {
        ...state,
        currentBuilding,
        currentFloor: keepFloor ? state.currentFloor : null,
      };
    }
    case UPDATE_CURRENT_BUILDING: {
      const { building } = action.payload;

      const currentBuilding = {
        ...building,
        numberOfFloors: building.floors?.length,
      };

      return {
        ...state,
        currentBuilding,
      };
    }
    case UPDATE_PROJECT_YEAR: {
      const { year } = action.payload;
      return {
        ...state,
        currentProject: {
          ...state.currentProject,
          year,
        },
      };
    }
    case UPDATE_NUMBER_OF_DEPARTMENTS: {
      const { numberOfDepartments } = action.payload;

      return {
        ...state,
        currentProject: {
          ...state.currentProject,
          numberOfDepartments,
        },
      };
    }
    case UPDATE_NUMBER_OF_SCENARIOS: {
      const { numberOfScenarios } = action.payload;

      return {
        ...state,
        currentProject: {
          ...state.currentProject,
          numberOfScenarios,
        },
      };
    }
    case GET_PROJECT_INFO_BY_ID_SUCCEEDED:
      if (state.currentBuilding && state.currentFloor) {
        const matchingBuilding = action.payload.project.buildings.find(
          (b) => b.id === state.currentBuilding.id
        );
        const matchingFloor = matchingBuilding?.floors.find(
          (f) => f.id === state.currentFloor.id
        );

        if (matchingFloor.totalFloorUsf !== state.currentFloor.totalFloorUsf) {
          return {
            ...state,
            currentProject: action.payload.project,
            currentBuilding: matchingBuilding,
            currentFloor: matchingFloor,
          };
        }
      }

      return {
        ...state,
        currentProject: action.payload.project,
      };
    case CLEAR_SELECTED_PROJECT: {
      return {
        ...state,
        currentProject: initialState.currentProject,
        currentProjectProfile: initialProjectProfile,
        projects: initialState.projects,
        currentFloor: initialState.currentFloor,
        currentBuilding: initialState.currentBuilding,
        colors: initialState.colors,
      };
    }
    case UPDATE_PROJECT_BUILDINGS_SUCCEEDED: {
      return {
        ...state,
        currentProject: {
          ...state.currentProject,
          buildings: action.payload.buildings,
        },
      };
    }
    case SET_CURRENT_FLOOR: {
      return {
        ...state,
        currentFloor: action.payload.floor,
      };
    }
    case TOGGLE_HEADCOUNT_MODAL: {
      return {
        ...state,
        openHeadcountModal: action.payload.openHeadcountModal,
      };
    }
    case FETCH_PROJECT_COLORS_SUCCEEDED: {
      const { colors } = action.payload;

      return {
        ...state,
        colors,
        currentProjectColors: {
          subcategories: buildCurrentProjectSubcategoryColors(colors),
        },
      };
    }
    case SET_CURRENT_PROJECT_COLORS: {
      return {
        ...state,
        currentProjectColors: action.payload.colors,
      };
    }
    case GET_USER_FAVORITE_PROJECTS_LEGACY:
      return {
        ...state,
        loadingProjects: true,
      };
    case GET_USER_FAVORITE_PROJECTS_LEGACY_SUCCEEDED: {
      return {
        ...state,
        userFavoriteProjectsLegacy: action.payload.userFavoriteProjectsLegacy,
        loadingProjects: false,
      };
    }
    case GET_ORGANIZATION_USERS_SUCCEEDED: {
      return {
        ...state,
        organizationUsers: action.payload.organizationUsers,
      };
    }
    case SET_PROJECT_DETAIL_SELECTED_TAB: {
      return {
        ...state,
        projectDetailSelectedTab: action.payload.selectedTab,
      };
    }
    case SET_PROJECT_DETAIL_NEXT_SELECTED_TAB: {
      return {
        ...state,
        projectDetailNextTab: action.payload.nextSelectedTab,
      };
    }
    case JOIN_CURRENT_PROJECT_SUCCEEDED: {
      return {
        ...state,
        currentProject: {
          ...state.currentProject,
          currentUserRole: action.payload,
        },
      };
    }
    case GET_PROJECT_EDITORS:
      return {
        ...state,
        projectEditorsList: {
          ...state.projectEditorsList,
          loading: true,
          queriedAtLeastOnce: true,
        },
      };
    case GET_PROJECT_EDITORS_SUCCEEDED:
      return {
        ...state,
        projectEditorsList: {
          loading: false,
          items: action.payload.projectEditors,
          queriedAtLeastOnce: true,
          total: action.payload.total,
        },
      };
    case GET_PROJECT_EDITORS_FAILED:
      return {
        ...state,
        projectEditorsList: {
          loading: false,
          items: [],
          queriedAtLeastOnce: true,
        },
      };
    case REMOVE_PROJECT_EDITOR:
      return {
        ...state,
        projectEditorsList: {
          ...state.projectEditorsList,
          loading: true,
        },
      };
    case REMOVE_PROJECT_EDITOR_SUCCEEDED:
      return {
        ...state,
        projectEditorsList: {
          queriedAtLeastOnce: true,
          items: state.projectEditorsList.items.filter(
            (pe) => pe.id !== action.payload
          ),
          total: state.projectEditorsList.total
            ? state.projectEditorsList.total - 1
            : state.projectEditorsList.total,
          loading: false,
        },
      };
    case REMOVE_PROJECT_EDITOR_FAILED:
      return {
        ...state,
        projectEditorsList: {
          ...state.projectEditorsList,
          loading: false,
        },
      };
    case CLEAN_UP_CURRENT_PROJECT:
      return {
        ...state,
        currentProject: null,
        projectEditorsList: initialState.projectEditorsList,
        currentBuilding: initialState.currentBuilding,
        currentFloor: initialState.currentFloor,
      };
    case LEAVE_PROJECT_SUCCEEDED: {
      const projectLeftIndex = state.projects.findIndex(
        ({ id }) => id === action.payload.projectId
      );

      return {
        ...state,
        projects: [
          ...state.projects.slice(0, projectLeftIndex),
          {
            ...state.projects[projectLeftIndex],
            isMember: false,
          },
          ...state.projects.slice(projectLeftIndex + 1),
        ],
      };
    }
    case JOIN_PROJECT_SUCCEEDED: {
      const projectJoinedIndex = state.projects.findIndex(
        ({ id }) => id === action.payload.id
      );

      return {
        ...state,
        projects: [
          ...state.projects.slice(0, projectJoinedIndex),
          {
            ...state.projects[projectJoinedIndex],
            isMember: true,
          },
          ...state.projects.slice(projectJoinedIndex + 1),
        ],
      };
    }
    case TRANSFER_PROJECT_OWNERSHIP_SUCCEEDED: {
      const projectTransferedIndex = state.projects.findIndex(
        ({ id }) => id === action.payload.projectId
      );

      return {
        ...state,
        projects: [
          ...state.projects.slice(0, projectTransferedIndex),
          {
            ...state.projects[projectTransferedIndex],
            projectOwner: {
              ...action.payload.user,
            },
            isMember: action.payload.preservePreviousOwnerAsEditor,
          },
          ...state.projects.slice(projectTransferedIndex + 1),
        ],
      };
    }
    case CREATE_USER_FAVORITE_PROJECT_SUCCEEDED: {
      const projectFavoritedIndex = state.projects.findIndex(
        ({ id }) => id === action.payload.projectId
      );

      return {
        ...state,
        projects: [
          ...state.projects.slice(0, projectFavoritedIndex),
          {
            ...state.projects[projectFavoritedIndex],
            isMarkedAsFavoriteByUser: true,
          },
          ...state.projects.slice(projectFavoritedIndex + 1),
        ],
      };
    }
    case DELETE_USER_FAVORITE_PROJECT_SUCCEEDED: {
      const projectUnfavoritedIndex = state.projects.findIndex(
        ({ id }) => id === action.payload.projectId
      );

      return {
        ...state,
        projects: [
          ...state.projects.slice(0, projectUnfavoritedIndex),
          {
            ...state.projects[projectUnfavoritedIndex],
            isMarkedAsFavoriteByUser: false,
          },
          ...state.projects.slice(projectUnfavoritedIndex + 1),
        ],
      };
    }

    case SET_PREVIOUSLY_VISITED_BENCHMARK_PROJECT_ID: {
      return {
        ...state,
        previouslyVisitedBenchmarkProjectId: action.payload.id,
      };
    }
    default:
      return state;
  }
};

export default reducer;
